import { useMemo } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import { useStoreCount } from '@/shared/features/store-count/presentation/hooks/useStoreCount';

type ContentPart =
  | { type: 'text'; value: string }
  | { type: 'link'; label: string; href: string; shouldRefresh?: boolean }
  | { type: 'storeCount' };

interface SeoItem {
  position?: number;
  icon?: 'map' | 'bag' | 'certified';
  imageUrl?: string;
  title: string;
  content: ContentPart[];
}

interface HomeSeoContentSectionProps {
  data: Record<string, unknown> & {
    storeCount?: number | string;
    items?: SeoItem[];
    showTitle?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
  /** When true, the first column title renders as `<h1>` (New Home SEO); defaults to `<h2>` for all columns. */
  firstColumnTitleAsH1?: boolean;
}

function Icon({ name }: { name: SeoItem['icon'] }) {
  const common = 'w-7 h-7 text-[#BBA3AE]';
  switch (name) {
    case 'map':
      return (
        <svg
          className={common}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3Z" />
          <path d="M9 3v15" />
          <path d="M15 6v15" />
        </svg>
      );
    case 'bag':
      return (
        <svg
          className={common}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 8h12l-1 13H7L6 8Z" />
          <path d="M9 8a3 3 0 0 1 6 0" />
          <path d="M9 12h.01" />
          <path d="M15 12h.01" />
        </svg>
      );
    case 'certified':
      return (
        <svg
          className={common}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 2l2.2 4.5L19 7l-3.5 3.4.8 4.8L12 13l-4.3 2.2.8-4.8L5 7l4.8-.5L12 2Z" />
          <path d="M8.5 20.5 12 18l3.5 2.5" />
        </svg>
      );
    default:
      return (
        <svg
          className={common}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M4 7h16" />
          <path d="M4 12h16" />
          <path d="M4 17h16" />
        </svg>
      );
  }
}

function renderContent(parts: ContentPart[], storeCount?: number | string) {
  return parts.map((part, idx) => {
    if (part.type === 'text') {
      return <span key={`t-${idx}`}>{part.value}</span>;
    }

    if (part.type === 'storeCount') {
      return (
        <span key={`s-${idx}`} className="font-medium text-[#7D6673]">
          {storeCount ?? '...'}
        </span>
      );
    }

    const isInternal = part.href.startsWith('/');
    const linkClass =
      'underline underline-offset-2 decoration-[#BBA3AE] hover:opacity-80 transition-opacity';

    return isInternal ? (
      <AppLink
        key={`l-${idx}`}
        to={part.href}
        className={linkClass}
        shouldRefresh={part.shouldRefresh}
      >
        {part.label}
      </AppLink>
    ) : (
      <a
        key={`l-${idx}`}
        href={part.href}
        className={linkClass}
        rel="noopener noreferrer"
      >
        {part.label}
      </a>
    );
  });
}

export default function HomeSeoContentSection({
  data,
  firstColumnTitleAsH1 = false,
}: HomeSeoContentSectionProps) {
  const countFallback = useMemo(() => {
    const c = data.storeCount;
    if (typeof c === 'number' && Number.isFinite(c)) return c;
    if (typeof c === 'string' && c.trim()) return c;
    return undefined;
  }, [data.storeCount]);

  const { storeCount } = useStoreCount(countFallback);
  const items = useMemo(() => (data.items || []) as SeoItem[], [data.items]);

  if (!items || items.length === 0) return null;

  return (
    <section className="w-full bg-[#FBF8F9] py-10 md:py-14">
      <div className="container-fluid max-w-7xl mx-auto">
        <div className="relative">
          {/* Desktop connector line */}
          <div className="hidden md:block absolute left-8 right-8 top-[40%] h-px bg-[#D8CBD3]" />

          <div className="grid gap-8 md:grid-cols-3">
            {items.map((item, index) => {
              const HeadingTag = firstColumnTitleAsH1 && index === 0 ? 'h1' : 'h2';
              return (
              <div
                key={`${item.title}-${index}`}
                className="relative h-full flex flex-col"
              >
                {/* Vertical connector (mobile + desktop) */}
                <div className="absolute left-8 top-16 w-px h-6 md:h-10 bg-[#D8CBD3]" />

                {/* Header row */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-md border border-[#E7DCE3] bg-white flex items-center justify-center aspect-square">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-9 h-9 object-contain"
                        loading="eager"
                        decoding="async"
                      />
                    ) : (
                      <Icon name={item.icon} />
                    )}
                  </div>
                  <HeadingTag className="text-[15px] leading-snug font-medium text-[#8C7381]">
                    {item.title}
                  </HeadingTag>
                </div>

                {/* Card */}
                <div className="mt-6 md:mt-8 flex-1 rounded-md border border-[#E7DCE3] bg-white p-6 text-[13px] leading-relaxed text-[#A28E99] z-10">
                  {renderContent(item.content, storeCount ?? undefined)}
                </div>
              </div>
            );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

